// ============================================
// CAROUSEL SEND PANEL
// src/components/CarouselSendPanel.jsx
//
// Shown when the chosen template is a carousel — in the chat's Send
// Template modal AND in the Broadcast modal.
//
// For each card the user:
//   - attaches the real image/video (uploaded to Meta once → media id)
//   - fills the card's text variables {{1}}, {{2}}…
//   - fills the ending of any link button that has a variable
//
// Media ids + values are remembered per template for 25 days
// (Meta keeps media ~30 days), so the next send is one click.
//
// In broadcasts, any value can contain {{name}} — it's replaced with
// each person's name when sending.
//
// Exports:
//   getCarouselComponent(template)            → CAROUSEL component or null
//   getCarouselProblem(template, cards)       → first missing thing, or ''
//   buildCarouselCards(template, cards, fill) → `cards` payload for /send-template
// ============================================
import React, { useEffect, useState } from 'react';
import { Upload, X, Image, Film, RefreshCw, CheckCircle2, Link as LinkIcon } from './icons/HeroshhiIcons';
import config from '../config';

const REMEMBER_DAYS = 25;
const storageKey = (name) => `carouselMedia:${name}`;

export const getCarouselComponent = (template) =>
  template?.components?.find(c => (c.type || '').toUpperCase() === 'CAROUSEL') || null;

// Pull out what we need from one approved card
const cardParts = (card) => {
  const comps = card?.components || [];
  const find = (t) => comps.find(c => (c.type || '').toUpperCase() === t);
  const header = find('HEADER');
  const bodyComp = find('BODY');
  const body = bodyComp?.text || '';
  const buttons = find('BUTTONS')?.buttons || [];
  const bodyVarCount = new Set((body.match(/\{\{(\d+)\}\}/g) || [])).size;
  const urlVarButtons = buttons
    .map((b, index) => ({ b, index }))
    .filter(({ b }) => (b.type || '').toUpperCase() === 'URL' && /\{\{\d+\}\}/.test(b.url || ''));
  // Meta returns the image approved with the template as a link here
  const sample = header?.example?.header_handle?.[0];
  return {
    format: (header?.format || 'IMAGE').toUpperCase(),   // IMAGE | VIDEO
    body, buttons, bodyVarCount, urlVarButtons,
    sampleUrl: typeof sample === 'string' && /^https?:\/\//.test(sample) ? sample : null,
    // Example values typed when the template was created (e.g. prices)
    bodyExamples: Array.isArray(bodyComp?.example?.body_text?.[0]) ? bodyComp.example.body_text[0] : []
  };
};

// A fresh card starts with the values given when the template was approved,
// so the user only changes what's different this time.
const emptyCard = (card) => {
  const { bodyVarCount, bodyExamples, urlVarButtons } = cardParts(card);
  const urlValues = {};
  urlVarButtons.forEach(({ b, index }) => {
    const ex = Array.isArray(b.example) ? b.example[0] : b.example;
    if (ex) urlValues[index] = String(ex);
  });
  return {
    mediaId: null,
    bodyValues: Array.from({ length: bodyVarCount }, (_, v) => String(bodyExamples[v] ?? '')),
    urlValues
  };
};

// Returns the first thing still missing, or '' when ready to send
export const getCarouselProblem = (template, cards) => {
  const carousel = getCarouselComponent(template);
  if (!carousel) return '';
  const list = carousel.cards || [];
  for (let i = 0; i < list.length; i++) {
    const { format, bodyVarCount, urlVarButtons } = cardParts(list[i]);
    const c = cards?.[i];
    if (!c?.mediaId) return `Add the ${format === 'VIDEO' ? 'video' : 'image'} for card ${i + 1}`;
    for (let v = 0; v < bodyVarCount; v++) {
      if (!String(c.bodyValues?.[v] || '').trim()) return `Fill {{${v + 1}}} on card ${i + 1}`;
    }
    for (const { b, index } of urlVarButtons) {
      if (!String(c.urlValues?.[index] || '').trim()) return `Fill the link ending for "${b.text}" on card ${i + 1}`;
    }
  }
  return '';
};

// fill: optional (text) => text, e.g. replace {{name}} per recipient in broadcasts
export const buildCarouselCards = (template, cards, fill = (t) => t) => {
  const carousel = getCarouselComponent(template);
  if (!carousel) return [];
  return (carousel.cards || []).map((card, i) => {
    const { format, buttons, bodyVarCount, urlVarButtons } = cardParts(card);
    const c = cards?.[i] || {};
    return {
      mediaId: c.mediaId,
      mediaType: format === 'VIDEO' ? 'video' : 'image',
      bodyParams: Array.from({ length: bodyVarCount }, (_, v) => fill(String(c.bodyValues?.[v] || '')).trim()),
      urlParams: urlVarButtons.map(({ index }) => ({ index, text: fill(String(c.urlValues?.[index] || '')).trim() })),
      quickReplies: buttons
        .map((b, idx) => ({ b, idx }))
        .filter(({ b }) => (b.type || '').toUpperCase() === 'QUICK_REPLY')
        .map(({ b, idx }) => ({ index: idx, payload: `${template.name}__card${i + 1}__${b.text}`.slice(0, 256) }))
    };
  });
};

// Show card text with the values filled in
const fillPreview = (text, values) =>
  text.replace(/\{\{(\d+)\}\}/g, (m, n) => (values?.[Number(n) - 1] || m));

export default function CarouselSendPanel({ template, cards, onChange, isBroadcast = false }) {
  const carousel = getCarouselComponent(template);
  const templateCards = carousel?.cards || [];
  const [uploading, setUploading] = useState(null);
  const [error, setError] = useState('');
  const [fromLastSend, setFromLastSend] = useState(false);
  const [usingApproved, setUsingApproved] = useState(false);

  // Load remembered media + values for this template (if still fresh)
  useEffect(() => {
    if (!template?.name) return;
    const empty = templateCards.map(emptyCard);
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey(template.name)) || 'null');
      const fresh = saved && Date.now() - saved.savedAt < REMEMBER_DAYS * 86400000;
      if (fresh && Array.isArray(saved.cards) && saved.cards.length === templateCards.length) {
        const merged = empty.map((e, i) => {
          const sv = saved.cards[i] || {};
          const bodyValues = e.bodyValues.map((ex, v) => (String(sv.bodyValues?.[v] || '').trim() ? sv.bodyValues[v] : ex));
          return { ...e, ...sv, bodyValues, urlValues: { ...e.urlValues, ...(sv.urlValues || {}) } };
        });
        onChange(merged);
        setFromLastSend(merged.some(c => c.mediaId));
        return;
      }
    } catch (e) { /* ignore bad storage */ }
    onChange(empty);
    setFromLastSend(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.name]);

  const remember = (next) => {
    try {
      const toStore = next.map(c => ({
        mediaId: c?.mediaId || null, mediaType: c?.mediaType, name: c?.name,
        bodyValues: c?.bodyValues || [], urlValues: c?.urlValues || {}
      }));
      localStorage.setItem(storageKey(template.name), JSON.stringify({ savedAt: Date.now(), cards: toStore }));
    } catch (e) { /* storage full or blocked — not critical */ }
  };

  const update = (i, patch) => {
    const next = templateCards.map((tc, idx) => ({ ...emptyCard(tc), ...((cards || [])[idx] || {}) }));
    next[i] = { ...next[i], ...patch };
    onChange(next);
    remember(next);
  };

  const upload = async (i, file, format) => {
    if (!file) return;
    const wantVideo = format === 'VIDEO';
    if (wantVideo && file.type !== 'video/mp4') { setError(`Card ${i + 1} needs an MP4 video`); return; }
    if (!wantVideo && !['image/jpeg', 'image/png'].includes(file.type)) { setError(`Card ${i + 1} needs a JPG or PNG image`); return; }

    setUploading(i);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${config.API_URL}/api/whatsapp/upload-media-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd
      }).then(x => x.json());
      if (!r.success) throw new Error(r.message || 'Upload failed');
      setFromLastSend(false);
      update(i, { ...r.data, previewUrl: URL.createObjectURL(file) });
    } catch (e) {
      setError(`Card ${i + 1}: ${e.message}`);
    } finally {
      setUploading(null);
    }
  };

  // 🖼️ Fill empty image cards with the images Meta approved with the template.
  // The server downloads each one and uploads it to Meta once → media id.
  const useApprovedImages = async () => {
    setUsingApproved(true);
    setError('');
    const next = templateCards.map((tc, idx) => ({ ...emptyCard(tc), ...((cards || [])[idx] || {}) }));
    let failed = 0;
    for (let i = 0; i < templateCards.length; i++) {
      const { format, sampleUrl } = cardParts(templateCards[i]);
      if (next[i].mediaId || format !== 'IMAGE' || !sampleUrl) continue;
      try {
        const r = await fetch(`${config.API_URL}/api/whatsapp/upload-media`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ mediaUrl: sampleUrl })
        }).then(x => x.json());
        if (!r.success || !r.mediaId) throw new Error(r.error || r.message || 'failed');
        next[i] = { ...next[i], mediaId: r.mediaId, mediaType: 'image', name: 'Approved image', previewUrl: sampleUrl };
      } catch (e) {
        failed++;
      }
    }
    onChange(next);
    remember(next);
    setUsingApproved(false);
    if (failed) setError(`${failed} card image(s) could not be copied — please add those by hand.`);
  };

  if (!carousel) return null;
  const problem = getCarouselProblem(template, cards);
  const canUseApproved = templateCards.some((tc, i) => {
    const p = cardParts(tc);
    return p.format === 'IMAGE' && p.sampleUrl && !cards?.[i]?.mediaId;
  });
  const hasAnyVars = templateCards.some(tc => {
    const p = cardParts(tc);
    return p.bodyVarCount > 0 || p.urlVarButtons.length > 0;
  });

  return (
    <div className="border border-gray-200 rounded-lg p-3 space-y-3 bg-white">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-700">Carousel cards</p>
        <span className={`text-xs font-medium ${problem ? 'text-gray-500' : 'text-green-600'}`}>
          {problem ? 'Not ready yet' : 'Ready to send'}
        </span>
      </div>

      {canUseApproved && (
        <button type="button" onClick={useApprovedImages} disabled={usingApproved}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-60">
          {usingApproved
            ? <><RefreshCw size={14} className="animate-spin" /> Copying images…</>
            : <><Image size={14} /> Use the images approved with this template</>}
        </button>
      )}

      {fromLastSend && (
        <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2.5 py-1.5 flex items-center gap-1.5">
          <CheckCircle2 size={13} /> Filled from your last send. Change anything you need.
        </p>
      )}
      {hasAnyVars && (
        <p className="text-xs text-gray-500">Values are filled from your approved template. Change any that are different this time.</p>
      )}
      {isBroadcast && hasAnyVars && (
        <p className="text-xs text-gray-500">
          Tip: type <code className="bg-gray-100 px-1 rounded">{'{{name}}'}</code> in any box to use each person's name.
        </p>
      )}

      <div className="flex gap-2 overflow-x-auto pb-1">
        {templateCards.map((card, i) => {
          const { format, body, buttons, bodyVarCount, urlVarButtons } = cardParts(card);
          const c = cards?.[i] || emptyCard(card);
          return (
            <div key={i} className="w-52 shrink-0 border border-gray-200 rounded-lg overflow-hidden bg-white flex flex-col">
              {/* Media */}
              <div className="relative h-28 bg-gray-100">
                {c.mediaId ? (
                  <>
                    {c.previewUrl
                      ? (format === 'VIDEO'
                          ? <video src={c.previewUrl} className="w-full h-full object-cover bg-black" muted />
                          : <img src={c.previewUrl} alt="" className="w-full h-full object-cover" />)
                      : <div className="w-full h-full flex flex-col items-center justify-center text-green-600 gap-1">
                          {format === 'VIDEO' ? <Film size={22} /> : <Image size={22} />}
                          <span className="text-[11px] px-2 text-center truncate max-w-full">{c.name || 'Attached'}</span>
                        </div>}
                    <button type="button" onClick={() => update(i, { mediaId: null, previewUrl: null, name: null })}
                      className="absolute top-1 right-1 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center text-gray-500 hover:text-red-600" title="Replace">
                      <X size={13} />
                    </button>
                  </>
                ) : (
                  <label className={`w-full h-full flex flex-col items-center justify-center gap-1 cursor-pointer ${uploading === i ? 'bg-blue-50' : 'hover:bg-blue-50/60'}`}>
                    {uploading === i
                      ? <RefreshCw size={18} className="animate-spin text-blue-500" />
                      : <Upload size={18} className="text-gray-400" />}
                    <span className="text-[11px] text-gray-500">
                      {uploading === i ? 'Uploading…' : `Add ${format === 'VIDEO' ? 'video (MP4)' : 'image'}`}
                    </span>
                    <input type="file" className="hidden" disabled={uploading !== null}
                      accept={format === 'VIDEO' ? 'video/mp4' : 'image/jpeg,image/png'}
                      onChange={e => { upload(i, e.target.files?.[0], format); e.target.value = ''; }} />
                  </label>
                )}
              </div>

              <div className="p-2 space-y-1.5 flex-1">
                <p className="text-[11px] font-semibold text-gray-500">Card {i + 1}</p>
                {body && <p className="text-xs text-gray-700 whitespace-pre-line">{fillPreview(body, c.bodyValues)}</p>}

                {/* Card text variables */}
                {Array.from({ length: bodyVarCount }).map((_, v) => (
                  <div key={v} className="flex items-center gap-1.5">
                    <span className="font-mono text-[10px] text-gray-400 w-8 shrink-0">{`{{${v + 1}}}`}</span>
                    <input
                      value={c.bodyValues?.[v] || ''}
                      onChange={e => {
                        const vals = [...(c.bodyValues || Array(bodyVarCount).fill(''))];
                        vals[v] = e.target.value;
                        update(i, { bodyValues: vals });
                      }}
                      placeholder="Value"
                      className="flex-1 min-w-0 px-2 py-1 border border-gray-300 rounded text-xs"
                    />
                  </div>
                ))}

                {/* Buttons, with link endings where needed */}
                <div className="space-y-1 pt-0.5">
                  {buttons.map((b, j) => {
                    const needsValue = urlVarButtons.some(u => u.index === j);
                    return (
                      <div key={j}>
                        <p className="text-[11px] text-blue-600 text-center border border-gray-100 rounded py-0.5 truncate">{b.text}</p>
                        {needsValue && (
                          <div className="mt-1">
                            <p className="text-[10px] text-gray-400 truncate flex items-center gap-1" title={b.url}>
                              <LinkIcon size={10} /> {(b.url || '').replace(/\{\{\d+\}\}.*/, '')}…
                            </p>
                            <input
                              value={c.urlValues?.[j] || ''}
                              onChange={e => update(i, { urlValues: { ...(c.urlValues || {}), [j]: e.target.value } })}
                              placeholder="Link ending"
                              className="w-full mt-0.5 px-2 py-1 border border-gray-300 rounded text-xs"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
      {problem && !error && <p className="text-xs text-amber-700">{problem}</p>}
      <p className="text-xs text-gray-400">Images: JPG or PNG, max 5 MB. Videos: MP4, max 16 MB.</p>
    </div>
  );
}